import { useLogin, useTwoFALogin } from "@/api/mutations/auth";
import { queryKeys } from "@/api/queryKeys";
import { ReactComponent as GoogleIcon } from "@/assets/icons/google-icon.svg";
import { OtpConfirmationDialog } from "@/components/otp-confirmation-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Message } from "@/components/ui/message";
import { ACCESS_TOKEN } from "@/constants/auth";
import { useTheme } from "@/hooks/use-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  username: z.string().min(2),
  password: z.string().min(6),
});

type FormData = z.infer<typeof schema>;

export const LoginPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutateAsync, isPending: isLoginPending } = useLogin();
  const { mutateAsync: twoFaLogin, isPending: isTwoFaLoginPending } = useTwoFALogin();
  const [openTwoFAConfirmation, setOpenTwoFAConfirmation] = useState(false);
  useTheme();

  const isPending = isTwoFaLoginPending || isLoginPending;

  const message = searchParams.get("message");
  useEffect(() => {
    if (message) {
      toast.error(message ?? "An error occurred during Google login.");
      setSearchParams(new URLSearchParams(), { replace: true });
    }
  }, [searchParams]);

  const { control, getValues, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = async (values: FormData) => {
    try {
      const result = await mutateAsync(values);
      if (result.data.twoFARequired) {
        return setOpenTwoFAConfirmation(true);
      }
      localStorage.setItem(ACCESS_TOKEN, result.data.accessToken);
      await queryClient.setQueryData([queryKeys.me], () => ({ data: result.data.user }));
      await queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
      if (result.data.user.shouldChangePassword) {
        return navigate("/reset-password", { state: { password: values.password } });
      }

      toast.success("Welcome back");
      navigate("/dashboard");
    } catch {
      toast.error("Invalid credentials");
    }
  };

  const onOtp = async (otp: string) => {
    const values = getValues();
    try {
      const result = await twoFaLogin({
        otp,
        ...values,
      });
      localStorage.setItem(ACCESS_TOKEN, result.data.accessToken);
      await queryClient.setQueryData([queryKeys.me], () => ({ data: result.data.user }));
      await queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
      if (result.data.user.shouldChangePassword) {
        return navigate("/reset-password", { state: { password: values.password } });
      }

      toast.success("Welcome back");
      navigate("/dashboard");
    } catch {
      toast.error("Wrong OTP provided");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background text-foreground">
      <OtpConfirmationDialog
        open={openTwoFAConfirmation}
        onOpenChange={setOpenTwoFAConfirmation}
        onSubmit={onOtp}
      />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle role="heading">Sign in</CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Controller
              name="username"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Input type="text" placeholder="Username" {...field} />
                  {error?.message && <Message variant="error">{error.message}</Message>}
                </>
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Input type="password" placeholder="Password" {...field} />
                  {error?.message && <Message variant="error">{error.message}</Message>}
                </>
              )}
            />

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>

            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="cursor-pointer w-full"
            onClick={() => {
              window.location.href = "/api/v1/auth/google-login";
            }}
          >
            <GoogleIcon className="mr-2 h-4 w-4" />
            Continue with Google
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
